// React
import React, { useState, useContext, useRef, useEffect } from 'react';

// Styles
import styles from './FormInput.module.scss';

// Third party
import nextId from 'react-id-generator';
import { trackPromise } from 'react-promise-tracker';
import 'date-fns';
import DateFnsUtils from '@date-io/date-fns';

// Contexts
import { DetailContext } from '../../../contexts/detail';

// Services
import { getListingData } from '../../../services/taxonomies';

// Shared
import eventBus from '../../../shared/eventBus';

// Components
import LocalizedFormInput from '../LocalizedFormInput/LocalizedFormInput';
import VersionControlDialgue from '../VersionControlDialgue/VersionControlDialgue';
import PriceInput from '../PriceInput/PriceInput';

// Hooks
import useMounted from '../../../hooks/useMounted';

// Material UI
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Input,
  InputLabel,
  MenuItem,
  TextField,
  Select,
  Radio,
  RadioGroup
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker
} from '@material-ui/pickers';

export default function FormInput({
  type,
  taxonomyName,
  label,
  inputValue,
  parameter,
  staticData,
  endpoint,
  getRows,
  disabled,
  absoluteDisabled,
  required,
  inputProps,
  priceType,
  InputProps,
  dependOn = null,
  disabledController = null,
  generatedValueKey,
  isCulturesSource = false,
  dispatchEventOnChange = true
}) {
  // Refeference (input)
  const input = useRef(null);

  // Detail Context
  const { saveCulturesDataSource, saveCultures } = useContext(DetailContext);

  // Disabled state
  const [isDisabled, setDisabled] = useState(disabled);

  // Disabled state (overrides the isDisabled state)
  const [isAbsoluteDisabled, setAbsoluteDisabled] = useState(absoluteDisabled);

  // If the component is mounted
  const mounted = useMounted();

  // Field value
  const [value, setValue] = useState('');

  // Checkbox
  const [checked, setChecked] = useState(inputValue);

  // Handle checkbox change
  const handleCheckboxChange = (event) => {
    const value_ = event.target.checked;
    setChecked(value_);

    // Dispatch global event
    if (dispatchEventOnChange) {
      eventBus.dispatch(`formInputChange_${parameter}`, {
        parameter,
        value: value_,
        code: null,
        label: null
      });
    }
  };

  // Date input
  const [selectedDate, setSelectedDate] = useState(inputValue);

  // Handle date input change
  const handleDateChange = (date, value_) => {
    setSelectedDate(date);

    // Dispatch global event
    if (dispatchEventOnChange) {
      eventBus.dispatch(`formInputChange_${parameter}`, {
        parameter,
        value: value_,
        code: null,
        label: null
      });
    }
  };

  // Autocomplete input
  const [multipleValues, setMultipleValues] = useState(inputValue);

  const handleMultipleValuesChange = (event, newValue) => {
    const newValueIds = newValue && newValue.map((option) => option.value);
    setMultipleValues(newValueIds);

    // Dispatch global event
    if (dispatchEventOnChange) {
      eventBus.dispatch(`formInputChange_${parameter}`, {
        parameter,
        value: newValueIds,
        code: null,
        label: null
      });
    }
  };

  // Handle select-autocomplete value change
  const handleSingularAutocompleteChange = (event, newValue) => {
    const newSingularValue = newValue ? newValue.value : '';
    setValue(newSingularValue);

    // Dispatch global event
    if (dispatchEventOnChange) {
      eventBus.dispatch(`formInputChange_${parameter}`, {
        parameter,
        value: newSingularValue,
        code: newValue && newValue.code ? newValue.code : null,
        label: newValue && newValue.label ? newValue.label : null
      });
    }
  };

  // Localization input (function to change the state from a nested component)
  const saveLocalizedValue = (newValue) => {

    const defaultTranslation = newValue.translations.find(
      (item) => item.defaultCulture === true
    );

    newValue["defaultCultureText"] = defaultTranslation.text;

    setValue(newValue);
  };

  // Keep reference to the inital passed function (we don't need to watch its changes, just need to invoke)
  const saveCulturesDataSourceRef = useRef(saveCulturesDataSource);

  // Handle text input/select change
  const handleInputChange = (event) => {
    const value_ = event.target.value;
    const dataElement = data.data.find((element) => element.value === value_);
    setValue(value_);

    // If this data is the source of the cultures to be used on the localized fields
    if (isCulturesSource) {
      saveCultures(value_);
    }

    // Dispatch global event
    if (dispatchEventOnChange) {
      eventBus.dispatch(`formInputChange_${parameter}`, {
        parameter,
        value: value_,
        code: dataElement && dataElement.code ? dataElement.code : null,
        label: dataElement && dataElement.label ? dataElement.label : null
      });
    }
  };

  // Handle Use Suggested Button Click
  const handleUseSuggestedButtonClick = (suggestedValue) => (event) => {
    if (type === 'localization') {
      eventBus.dispatch('suggestedValueButtonClick', {
        suggestedValue: suggestedValue
      });
    } else {
      setValue(suggestedValue);
    }
  };

  // Data
  const [data, setData] = useState(
    staticData || {
      data: []
    }
  );

  // Keep reference to the inital passed function (we don't need to watch its changes, just need to invoke)
  const getRowsRef = useRef(getRows);

  // Values from the parent/required fields
  const [dependOnValue, setDependOnValue] = useState([]);

  // Parent/required fields have changed (values)
  useEffect(() => {
    if (dependOn && typeof dependOn === 'object' && dependOn.length > 0) {
      // Add event to each parent/required field
      dependOn.forEach((dependOn_) => {
        eventBus.on(`formInputChange_${dependOn_.control}`, (data_) => {
          if (mounted.current) {
            const filter = dependOn_.filter;
            const value_ = data_.value;

            // Set parent/required fields values
            setDependOnValue((currentDependOnValue) => {
              let newDependOnValue = null;

              // Index to update
              const index = currentDependOnValue.findIndex(
                (item) => item.filter === filter
              );

              // Update item
              if (index >= 0) {
                newDependOnValue = currentDependOnValue.map((item) =>
                  item.filter === filter ? { ...item, value: value_ } : item
                );
              }
              // New item
              else {
                newDependOnValue = [
                  ...currentDependOnValue,
                  { filter, value: value_ }
                ];
              }

              // How many parent/required fields are populated
              const newDependOnValueCounter = newDependOnValue.filter(
                (item) => item.value && item.value !== ''
              ).length;

              // Enable the field in case all parent/required fields are populated
              if (newDependOnValueCounter === dependOn.length) {
                setDisabled(false);
              }
              // Disable the field in case not all parent/required fields are populated
              else {
                setDisabled(true);
              }

              // Return new values/state
              return newDependOnValue;
            });
          }
        });
      });
    }
  }, [dependOn, mounted]);

  // Parent/required fields have changed (values)
  useEffect(() => {
    if (disabledController) {
      // Add event to each parent/required field
      eventBus.on(`formInputChange_${disabledController.control}`, (data_) => {
        if (mounted.current) {
          if (data_.value === disabledController.value) {
            setAbsoluteDisabled(false);
          } else {
            setAbsoluteDisabled(true);

            // Reset values (all values, just in case)
            setValue('');
            setSelectedDate('');
            setMultipleValues([]);
            setChecked(false);
          }
        }
      });
    }
  }, [disabledController, mounted]);

  // Get data
  useEffect(() => {
    // Dynamic data
    if ((type === 'select' || type === 'multiple-select' || type === 'select-autocomplete') && endpoint) {
      // How many parent/required fields are populated
      const dependOnValueCounter = dependOnValue.filter(
        (item) => item.value && item.value !== ''
      ).length;

      // If there is no parent/required fields
      // OR
      // If the parent/required fields are all populated
      if (
        !(dependOn && typeof dependOn === 'object' && dependOn.length > 0) ||
        dependOnValueCounter === dependOn.length
      ) {
        // Reset the value and options before fetching new options
        setValue('');
        setData({ data: [] });

        // TEMP: make sure countryId comes first
        let sortedDependOnValue = [];
        if (dependOn) {
          dependOn.forEach((dependOn_) => {
            sortedDependOnValue.push({
              filter: dependOn_.filter,
              value: dependOnValue.find(
                (dependOnValue_) => dependOnValue_.filter === dependOn_.filter
              ).value
            });
          });
        }

        const promise = getListingData({
          endpoint,
          getRows: getRowsRef.current,
          dependOnValue: sortedDependOnValue
        });

        trackPromise(
          promise.then((newData) => {
            if (
              mounted.current &&
              newData &&
              newData.data &&
              typeof newData.data === 'object'
            ) {
              setData(newData);
              setValue(newData.data.find(option => option.value === inputValue) ? inputValue : '');

              const dataElement = newData.data.find(
                (element) => element.value === inputValue
              );

              // If this data is the source of the cultures to be used on the localized fields
              if (isCulturesSource) {
                saveCulturesDataSourceRef.current(newData.data, inputValue);
              }

              // Dispatch the event for field dependence
              if (dispatchEventOnChange) {
                eventBus.dispatch(`formInputChange_${parameter}`, {
                  parameter,
                  value: inputValue,
                  code:
                    dataElement && dataElement.code ? dataElement.code : null,
                  label:
                    dataElement && dataElement.label ? dataElement.label : null
                });
              }
            }
          })
        );
      }
    } else {
      const val =
        typeof inputValue !== 'undefined' &&
        inputValue !== null &&
        inputValue.hasOwnProperty('key') &&
        inputValue.hasOwnProperty('text')
          ? inputValue.text
          : inputValue;
      setValue(val);

      // Dispatch the event for field dependence
      if (dispatchEventOnChange) {
        eventBus.dispatch(`formInputChange_${parameter}`, {
          parameter,
          value: inputValue,
          code: null,
          label: null
        });
      }
    }
  }, [
    type,
    endpoint,
    inputValue,
    dependOnValue,
    dependOn,
    parameter,
    isCulturesSource,
    mounted,
    dispatchEventOnChange
  ]);

  // Disable
  useEffect(() => {
    eventBus.on(`formInputMakeDisabled_${parameter}`, () => {
      setDisabled(true);
    });
  }, [parameter]);

  // New taxonomy was added
  useEffect(() => {
    eventBus.on('taxonomyAdded', (data_) => {
      if (
        mounted.current &&
        data_.taxonomyName === taxonomyName &&
        data_.inputParameter === parameter &&
        data_.newRows &&
        data_.newRows.length > 0
      ) {
        const inputValue = data_.newRows[0].value;

        setData((oldData) => {
          const newData = {
            data: [...oldData.data, ...data_.newRows]
          };

          const dataElement = newData.data.find(
            (element) => element.value === inputValue
          );

          // Dispatch the event for field dependence
          if (dispatchEventOnChange) {
            eventBus.dispatch(`formInputChange_${parameter}`, {
              parameter,
              value: inputValue,
              code: dataElement && dataElement.code ? dataElement.code : null,
              label: dataElement && dataElement.label ? dataElement.label : null
            });
          }

          return newData;
        });

        setValue(inputValue);
      }
    });
  }, [parameter, taxonomyName, mounted, dispatchEventOnChange]);

  const control = (() => {
    if (type === 'select') {
      const uniqueId = nextId('tps-unique-id-');

      return (
        <FormControl
          variant="outlined"
          fullWidth={true}
          required={required}
          disabled={isDisabled}>
          <InputLabel id={uniqueId}>{label}</InputLabel>
          <Select
            inputRef={input}
            classes={{ select: styles.select }}
            labelId={uniqueId}
            name={parameter}
            label={label}
            value={value}
            onChange={handleInputChange}>
            <MenuItem value="">
              <em>Select</em>
            </MenuItem>
            {data.data &&
              data.data.map((item, i) => {
                return (
                  <MenuItem key={i} value={item.value}>
                    {item.label}
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>
      );
    } else if (type === 'select-autocomplete') {
      return (
        <>
          <Input
              type="hidden"
              name={parameter}
              value={value}
              style={{ display: 'none' }}
          />
          <Autocomplete
            options={data.data}
            loading={!(data && data.data && typeof data.data.length > 0)}
            loadingText="No data"
            getOptionLabel={option => {
              if (option) {
                return option.label ? option.label : `(No label is configured for option with value = ${option.value})`;
              } else {
                return '';
              }
            }}
            value={data.data.find(option => option.value === value) ? data.data.find(option => option.value === value) : null}
            getOptionSelected={(option, value) => {
              return option.value === value.value;
            }}
            onChange={handleSingularAutocompleteChange}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={input}
                className={styles['input-text']}
                variant="outlined"
                label={label}
              />
            )}
            fullWidth={true}
            required={required}
            disabled={isAbsoluteDisabled || isDisabled}
            id={`autocomplete-${parameter}`}
          />
        </>
      );
    } else if (type === 'radio') {
      return (
        <FormControl
          variant="outlined"
          fullWidth={true}
          required={required}
          disabled={isDisabled}
          component="fieldset">
          <FormLabel component="legend">{label}</FormLabel>
          <RadioGroup
            aria-label={label}
            name={parameter}
            value={value}
            onChange={handleInputChange}
            row>
            {data.data &&
              data.data.map((item, i) => {
                return (
                  <FormControlLabel
                    key={i}
                    value={item.value}
                    control={<Radio />}
                    label={item.label}
                  />
                );
              })}
          </RadioGroup>
        </FormControl>
      );
    } else if (type === 'checkbox') {
      return (
        <FormControlLabel
          control={
            <Checkbox
              name={parameter}
              value="true"
              checked={checked}
              onChange={handleCheckboxChange}
            />
          }
          label={label}
        />
      );
    } else if (type === 'datetime') {
      return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <KeyboardDatePicker
            disableToolbar
            disabled={isDisabled}
            inputVariant="outlined"
            format="yyyy-MM-dd"
            fullWidth={true}
            label={label}
            name={parameter}
            value={selectedDate ?? null}
            onChange={handleDateChange}
            KeyboardButtonProps={{
              'aria-label': `Change ${label}`
            }}
          />
        </MuiPickersUtilsProvider>
      );
    } else if (type === 'multiple-select') {
      return (
        <>
          <Input
            type="hidden"
            name={parameter}
            value={JSON.stringify(multipleValues)}
            style={{ display: 'none' }}
          />
          <Autocomplete
            multiple
            options={data.data}
            loading={!(data && data.data && typeof data.data.length > 0)}
            loadingText="No data"
            getOptionLabel={(option) => option.label}
            value={data.data.filter((option) =>
              multipleValues.includes(option.value)
            )}
            filterSelectedOptions={true}
            getOptionSelected={(option, value) => {
              const isValid = (a, b) =>
                a.label === b.label && a.value === b.value;
              let valid = false;
              if (Array.isArray(value)) {
                value.forEach((item) => {
                  if (isValid(option, item)) {
                    valid = true;
                  }
                });
              } else {
                if (isValid(option, value)) {
                  valid = true;
                }
              }
              return valid;
            }}
            onChange={handleMultipleValuesChange}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={input}
                className={styles['input-text']}
                variant="outlined"
                label={label}
              />
            )}
            fullWidth={true}
            required={required}
            disabled={isAbsoluteDisabled || isDisabled}
            id={`autocomplete-${parameter}`}
          />
        </>
      );
    } else if (type === 'hidden') {
      return (
        <Input
          type="hidden"
          name={parameter}
          value={value}
          style={{ display: 'none' }}
        />
      );
    } else if (type === 'localization') {
      return (
        <>
          <div className={styles['translate-icon']}>
            <LocalizedFormInput
              title={label}
              value={value}
              setValue={saveLocalizedValue}
              disabled={isDisabled}
            />
          </div>
          <Input
            type="hidden"
            name={parameter}
            value={JSON.stringify(value)}
            style={{ display: 'none' }}
          />
          <TextField
            inputRef={input}
            variant="outlined"
            label={label}
            name={`${parameter}_readOnly`}
            fullWidth={true}
            required={required === true}
            disabled={isDisabled}
            inputProps={{ readOnly: true }}
            value={
              value && typeof value.defaultCultureText !== 'undefined'
                ? value.defaultCultureText
                : value
            }
            onChange={handleInputChange}
          />
        </>
      );
    } else if (type === 'version-control') {
      return(
        <>
          <VersionControlDialgue
            inputProps={inputProps}
            />
        </>
      );
    } else if (type === 'price') {

      return (
        <PriceInput
          inputRef={input}
          className={styles['input-text']}
          variant="outlined"
          name={parameter}
          label={label}
          fullWidth={true}
          required={required === true}
          disabled={isDisabled}
          value={value}
          onChange={handleInputChange}
          priceType={priceType}
        />
      );

    }

    return (
      <TextField
        inputRef={input}
        className={styles['input-text']}
        variant="outlined"
        name={parameter}
        label={label}
        fullWidth={true}
        required={required === true}
        disabled={isDisabled}
        inputProps={inputProps}
        InputProps={InputProps}
        value={value}
        onChange={handleInputChange}
      />
    );

  })();

  if (generatedValueKey) {
    return (
      <DetailContext.Consumer>
        {({ generatedValues }) => (
          <>
            {control}
            <div className={styles['suggested']}>
              <div>
                Suggested value:{' '}
                {generatedValues[generatedValueKey] ? (
                  generatedValues[generatedValueKey]
                ) : (
                  <em>not enough information to generate a value</em>
                )}
              </div>
              <Button
                variant="outlined"
                className={styles['suggested-button']}
                color="secondary"
                size="small"
                onClick={handleUseSuggestedButtonClick(
                  generatedValues[generatedValueKey]
                )}>
                Use suggested value
              </Button>
            </div>
          </>
        )}
      </DetailContext.Consumer>
    );
  }

  return control;
}
